/**
 * Program duration resolution.
 *
 * Lives in lib/ rather than inside the ProgramComplete component so it can be
 * unit-tested — vitest's node environment cannot import a .tsx.
 */

/**
 * Programs whose ID does not follow the "Nweek" naming convention.
 * Verified against each program's own `metadata.duration`; there is a test
 * (lib/__tests__/programDurationSource.test.ts) pinning them together.
 */
const KNOWN_PROGRAM_WEEKS: Record<string, number> = {
  'chassis-cut-operator-hybrid': 12,
  // The file is named hstFatLoss12Week.ts but declares 14 weeks
  // (1 max-finding + 11 training + 2 deload) — trust metadata, not the name.
  'hst-fat-loss-framework': 14,
};

/**
 * Get program duration in weeks from a program ID.
 *
 * SYNCHRONOUS BEST-EFFORT ONLY. The authoritative value is
 * `program.metadata.duration`; prefer it wherever you can await the program
 * cache (ProgramNavigator does, and lib/dayTracking's
 * resolveProgramDurationWeeks does). This exists for render-time callers.
 *
 * The old version regexed the ID and fell back to 13, which disagreed with the
 * real metadata for programs whose ID lacks a week count — so the 12-week
 * Chassis Cut was reported as 13, the navigator offered a Week 13 that does not
 * exist, and jumping there stranded the user on a fake "Program Complete"
 * screen six weeks early.
 */
export function getProgramDurationWeeks(programId: string | undefined): number {
  if (!programId) return 13;

  const id = programId.toLowerCase();

  if (KNOWN_PROGRAM_WEEKS[id] !== undefined) return KNOWN_PROGRAM_WEEKS[id];

  const weekMatch = id.match(/(\d+)[-]?week/);
  if (weekMatch) {
    return parseInt(weekMatch[1], 10);
  }

  // Family fallbacks for ID variants not listed above.
  if (id.includes('chassis')) return 12;
  if (id.includes('hst')) return 14;
  if (id.includes('sustainment') && id.includes('6')) return 6;
  if (id.includes('prenatal') || id.includes('preeclampsia')) return 37;

  return 13;
}
